/**
 * CSV export for leads (per-venue).
 * Pure browser util — no React, no Firestore. Receives an array of lead docs.
 *
 * Safety:
 * - UTF-8 BOM so Excel-Windows opens es-MX accents correctly.
 * - Cells with `,`, `"`, newlines, or leading `=/+/-/@` are quoted and escaped;
 *   formula-leading cells additionally get a `'` prefix to neutralize CSV
 *   injection in Sheets/Excel.
 */

const CSV_INJECTION_FIRST_CHARS = new Set(["=", "+", "-", "@"]);

const toJsDate = (val) => {
  if (val == null) return null;
  if (val instanceof Date) return val;
  if (typeof val.toDate === "function") return val.toDate();
  if (typeof val.seconds === "number") return new Date(val.seconds * 1000);
  if (typeof val === "string" || typeof val === "number") return new Date(val);
  return null;
};

const isoOrEmpty = (val) => {
  const d = toJsDate(val);
  if (!d || isNaN(d.getTime())) return "";
  return d.toISOString();
};

// Format interestDates[] entries as "YYYY-MM-DD a YYYY-MM-DD" joined by "; ".
const fmtDateOnly = (d) => {
  const x = toJsDate(d);
  if (!x || isNaN(x.getTime())) return "";
  return x.toISOString().slice(0, 10);
};

const fmtInterestDates = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return "";
  return arr
    .map((entry) => {
      // Each entry is typically { start, end } (Timestamps) — fall back to flat date.
      const s = fmtDateOnly(entry?.start ?? entry);
      const e = fmtDateOnly(entry?.end ?? entry);
      if (s && e && s !== e) return `${s} a ${e}`;
      return s || e;
    })
    .filter(Boolean)
    .join("; ");
};

const escapeCell = (raw) => {
  let v = raw == null ? "" : String(raw);
  // Neutralize formula injection: prefix `'` so spreadsheets treat it as text.
  if (v.length > 0 && CSV_INJECTION_FIRST_CHARS.has(v[0])) {
    v = `'${v}`;
  }
  // Quote if the value contains `,`, `"`, CR, or LF.
  if (/[",\r\n]/.test(v)) {
    v = `"${v.replace(/"/g, '""')}"`;
  }
  return v;
};

const slugify = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "venue";

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
};

const HEADERS = [
  "Nombre",
  "Email",
  "Teléfono",
  "País",
  "Tema",
  "Mensaje",
  "Notas",
  "Origen",
  "Estado",
  "Fecha de creación",
  "Última actualización",
  "Fechas de interés",
  "Convertido a evento",
  "ID evento",
  "Cotizaciones generadas",
  "ID lead",
];

const rowFor = (lead) => {
  const origin = lead.origin || "";
  const originLabel =
    !origin || origin.toLowerCase() === "venueboard" ? "Venueboard" : origin;
  return [
    lead.name || "",
    lead.userEmail || lead.email || "",
    lead.phone || "",
    lead.country || "",
    lead.topic || "",
    lead.message || "",
    lead.notes || "",
    originLabel,
    lead.status || "",
    isoOrEmpty(lead.createdAt),
    isoOrEmpty(lead.updatedAt),
    fmtInterestDates(lead.interestDates),
    lead.convertedToEventId ? "Sí" : "No",
    lead.convertedToEventId || "",
    Array.isArray(lead.cotizacionHistory) ? lead.cotizacionHistory.length : 0,
    lead.id || "",
  ];
};

export function buildLeadsCSV(leads) {
  const lines = [HEADERS.map(escapeCell).join(",")];
  for (const l of leads) {
    lines.push(rowFor(l).map(escapeCell).join(","));
  }
  // UTF-8 BOM (`﻿`) — Excel-Windows needs this to render accents right.
  return `﻿${lines.join("\r\n")}\r\n`;
}

export function downloadLeadsCSV({ leads, venueName, rangeKey }) {
  const csv = buildLeadsCSV(leads);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `leads_${slugify(venueName)}_${rangeKey || "rango"}_${todayYmd()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
